import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useEffect, useRef, useState } from 'react';
import { useLocalizeMessage } from 'libs.nucleus';
import { ConsentChoiceGroupHeader } from './consent_choice_group_header.component';
import { ConsentChoiceOptionMarker } from './consent_choice_option_marker.component';
import { ConsentElementMarker } from './consent_element_marker.component';
import { nextRoveIndex } from './overlay_keyboard';
import { isChoiceElementType } from '../../constants/consent_element_types.constants';
const choiceItemIdsOnPage = (el, pageNumber) => {
    const pageOptions = (el.config.ec__options ?? []).filter((option) => option.page === pageNumber);
    return [
        ...pageOptions.map((option) => `opt-${el.id}-${option.id}`),
        ...(pageOptions.length > 0 ? [`hdr-${el.id}-p${pageNumber}`] : []),
    ];
};
export const ConsentElementOverlay = ({ pageNumber, totalPages, elements, selectedElementId, onUpdateElement, onUpdateElementPosition, onDeleteElement, onConfigureElement, onSelectElement, onAnnounce = () => undefined, focusedItemId: focusedItemIdProp, onFocusItem, }) => {
    const translate = useLocalizeMessage();
    const pages = totalPages ?? 1;
    const overlayRef = useRef(null);
    // Roving tabindex needs a finer id than selectedElementId: a selected choice has many markers
    // (header + each option) and only one of them should be the tab stop. Local state is the
    // fallback for tests that render the overlay alone; the editor lifts it so a page move can
    // remount the same item on another overlay.
    const [internalFocusedItemId, setInternalFocusedItemId] = useState(null);
    const focusedItemId = focusedItemIdProp ?? internalFocusedItemId;
    const setFocusedItemId = onFocusItem ?? setInternalFocusedItemId;
    // Non-choice elements are placed as a single marker on `element.page`.
    const simpleElements = elements.filter((el) => !isChoiceElementType(el.type) && el.page === pageNumber);
    // Choice elements place each option independently, so we render per-option on the option's own page.
    const choiceElements = elements.filter((el) => isChoiceElementType(el.type));
    const itemIds = [
        ...simpleElements.map((el) => `el-${el.id}`),
        ...choiceElements.flatMap((el) => choiceItemIdsOnPage(el, pageNumber)),
    ];
    const tabStopId = (focusedItemId && itemIds.includes(focusedItemId) ? focusedItemId : null) ?? itemIds[0] ?? null;
    const itemIdKey = itemIds.join('|');
    const prevItemIdKeyRef = useRef(itemIdKey);
    const prevFocusedRef = useRef(focusedItemId ?? null);
    // Restore focus when the focused item remounts on this page (Page Up/Down) or when
    // delete moves the tab stop to a neighbor. Ignore itemIdKey changes that only add a
    // new marker (click-to-place): those leave focus on <body> and must not yank it back
    // onto the previously focused overlay.
    useEffect(() => {
        const ids = itemIdKey ? itemIdKey.split('|') : [];
        const prevIds = prevItemIdKeyRef.current ? prevItemIdKeyRef.current.split('|') : [];
        const prevFocused = prevFocusedRef.current;
        prevItemIdKeyRef.current = itemIdKey;
        prevFocusedRef.current = focusedItemId ?? null;
        if (!focusedItemId || !ids.includes(focusedItemId)) {
            return;
        }
        if (document.activeElement !== document.body) {
            return;
        }
        const appeared = !prevIds.includes(focusedItemId);
        const focusMoved = prevFocused !== focusedItemId;
        if (!appeared && !focusMoved) {
            return;
        }
        overlayRef.current?.querySelector(`[data-overlay-item="${focusedItemId}"]`)?.focus();
    }, [focusedItemId, itemIdKey]);
    const focusNeighbor = (removedIds) => {
        const removed = new Set(removedIds);
        const idx = itemIds.findIndex((id) => removed.has(id));
        const remaining = itemIds.filter((id) => !removed.has(id));
        if (remaining.length === 0) {
            setFocusedItemId(null);
            return;
        }
        const fallback = idx < 0 ? 0 : Math.min(idx, remaining.length - 1);
        setFocusedItemId(remaining[fallback]);
    };
    const handleOverlayKeyDown = (e) => {
        if (e.key !== 'Tab') {
            return;
        }
        const items = [...e.currentTarget.querySelectorAll('[data-overlay-item]')];
        if (items.length < 2) {
            return;
        }
        const active = document.activeElement;
        const current = items.findIndex((el) => el === active || (active instanceof Node && el.contains(active)));
        if (current < 0) {
            return;
        }
        const next = nextRoveIndex(current, items.length, e.shiftKey ? -1 : 1);
        if (next === null) {
            return;
        }
        e.preventDefault();
        items[next].focus();
    };
    return (
    // never intercept pointer events — the parent page-wrapper handles placement clicks
    _jsxs("div", { ref: overlayRef, role: 'application', "data-testid": `consent-overlay-page-${pageNumber}`, "aria-label": translate('Document page {page} overlays', { page: pageNumber }), "aria-describedby": 'consent-overlay-kbd-help', onKeyDown: handleOverlayKeyDown, style: {
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            pointerEvents: 'none',
            overflow: 'visible',
        }, children: [simpleElements.map((el) => (
            // re-enable pointer events just for the markers so they stay interactive
            _jsx("div", { style: { pointerEvents: 'auto' }, children: _jsx(ConsentElementMarker, { element: el, isSelected: selectedElementId === el.id, isTabStop: tabStopId === `el-${el.id}`, totalPages: pages, onSelect: (id) => {
                        setFocusedItemId(`el-${id}`);
                        onSelectElement(id);
                    }, onUpdate: onUpdateElement, onUpdatePosition: onUpdateElementPosition, onDelete: (id) => {
                        focusNeighbor([`el-${id}`]);
                        onDeleteElement(id);
                    }, onConfigureClick: onConfigureElement, onAnnounce: onAnnounce }) }, el.id))), choiceElements.map((el) => {
                const pageOptions = (el.config.ec__options ?? []).filter((option) => option.page === pageNumber);
                const headerId = `hdr-${el.id}-p${pageNumber}`;
                return (_jsxs(Fragment, { children: [pageOptions.map((option) => (_jsx("div", { style: { pointerEvents: 'auto' }, children: _jsx(ConsentChoiceOptionMarker, { element: el, option: option, isSelected: selectedElementId === el.id, isTabStop: tabStopId === `opt-${el.id}-${option.id}`, totalPages: pages, onSelect: (id) => {
                                    setFocusedItemId(`opt-${el.id}-${option.id}`);
                                    onSelectElement(id);
                                }, onUpdate: onUpdateElement, onDeleteElement: (id) => {
                                    focusNeighbor(choiceItemIdsOnPage(el, pageNumber));
                                    onDeleteElement(id);
                                }, onItemRemoved: () => {
                                    const removing = pageOptions.length === 1 ? choiceItemIdsOnPage(el, pageNumber) : [`opt-${el.id}-${option.id}`];
                                    const remainingOnPage = itemIds.filter((id) => !removing.includes(id));
                                    if (remainingOnPage.length > 0) {
                                        focusNeighbor(removing);
                                        return;
                                    }
                                    const other = (el.config.ec__options ?? []).find((o) => o.id !== option.id);
                                    setFocusedItemId(other ? `opt-${el.id}-${other.id}` : null);
                                }, onAnnounce: onAnnounce }) }, `${el.id}-${option.id}`))), pageOptions.length > 0 && (_jsx("div", { style: { pointerEvents: 'auto' }, children: _jsx(ConsentChoiceGroupHeader, { element: el, pageNumber: pageNumber, pageOptions: pageOptions, isSelected: selectedElementId === el.id, isTabStop: tabStopId === headerId, totalPages: pages, onSelect: (id) => {
                                    setFocusedItemId(headerId);
                                    onSelectElement(id);
                                }, onUpdate: onUpdateElement, onDelete: (id) => {
                                    focusNeighbor(choiceItemIdsOnPage(el, pageNumber));
                                    onDeleteElement(id);
                                }, onPageChange: (nextPage) => setFocusedItemId(`hdr-${el.id}-p${nextPage}`), onAnnounce: onAnnounce }) }))] }, el.id));
            })] }));
};
